import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../config/apiService';

// Historic Reports — how the program is doing over a wider time frame.
//   Points      : earned vs. possible by month + site group (with a bar)
//   Recordables : which site group had a recordable, month by month
//   Criteria    : pass rate per qualification criterion (what fails most)
//   Winners     : the monthly drawing winners
const TABS = [
    { key: 'points',      label: 'Points' },
    { key: 'recordables', label: 'Recordables' },
    { key: 'criteria',    label: 'Criteria' },
    { key: 'winners',     label: 'Winners' },
];

const RANGES = [
    { v: 6,  label: 'Last 6 months' },
    { v: 12, label: 'Last 12 months' },
    { v: 24, label: 'Last 24 months' },
    { v: 120, label: 'All time' },
];

const monthLabel = (y, m) => new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' });
const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);

function HistoricReportsManager() {
    const [tab, setTab] = useState('points');
    const [monthsBack, setMonthsBack] = useState(12);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        let cancelled = false;
        const call = { points: adminAPI.reportPoints, recordables: adminAPI.reportRecordables,
                       criteria: adminAPI.reportCriteria, winners: adminAPI.reportWinners }[tab];
        setLoading(true); setErr('');
        call(monthsBack)
            .then((r) => { if (!cancelled) setRows(r.data || []); })
            .catch((e) => { if (!cancelled) { setRows([]); setErr(e?.response?.data || e?.message || 'Failed to load'); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [tab, monthsBack]);

    // Points: group by month so each month shows its sites plus a total line.
    const pointMonths = useMemo(() => {
        if (tab !== 'points') return [];
        const map = new Map();
        for (const r of rows) {
            const k = `${r.year}-${r.month}`;
            if (!map.has(k)) map.set(k, { year: r.year, month: r.month, sites: [], earned: 0, possible: 0, sups: 0, earning: 0 });
            const g = map.get(k);
            g.sites.push(r);
            g.earned += r.pointsEarned; g.possible += r.pointsPossible;
            g.sups += r.supervisors; g.earning += r.supervisorsEarning;
        }
        return [...map.values()];
    }, [rows, tab]);

    // Recordables: pivot to month rows x site-group columns.
    const recordGrid = useMemo(() => {
        if (tab !== 'recordables') return { groups: [], months: [] };
        const groups = [...new Set(rows.map((r) => r.siteGroup))].sort();
        const map = new Map();
        for (const r of rows) {
            const k = `${r.year}-${r.month}`;
            if (!map.has(k)) map.set(k, { year: r.year, month: r.month, cells: {} });
            map.get(k).cells[r.siteGroup] = r;
        }
        return { groups, months: [...map.values()] };
    }, [rows, tab]);

    // Winners: group by month then site.
    const winnerMonths = useMemo(() => {
        if (tab !== 'winners') return [];
        const map = new Map();
        for (const r of rows) {
            const k = `${r.year}-${r.month}`;
            if (!map.has(k)) map.set(k, { year: r.year, month: r.month, sites: new Map() });
            const g = map.get(k);
            if (!g.sites.has(r.site)) g.sites.set(r.site, []);
            g.sites.get(r.site).push(r);
        }
        return [...map.values()].map((g) => ({ ...g, sites: [...g.sites.entries()] }));
    }, [rows, tab]);

    const active = TABS.find((t) => t.key === tab);

    return (
        <div style={styles.wrap}>
            <style>{PRINT_CSS}</style>

            <div className="hr-noprint">
                <div style={styles.eyebrow}>Settings</div>
                <h2 style={styles.h2}>Historic Reports</h2>
                <p style={styles.muted}>How the program is trending across months and sites.</p>

                <div style={styles.tabBar}>
                    {TABS.map((t) => (
                        <button key={t.key} onClick={() => setTab(t.key)} style={{ ...styles.tab, ...(tab === t.key ? styles.tabActive : {}) }}>
                            {t.label}
                        </button>
                    ))}
                </div>

                <div style={styles.controls}>
                    <label style={styles.label}>Range</label>
                    <select value={monthsBack} onChange={(e) => setMonthsBack(Number(e.target.value))} style={styles.input}>
                        {RANGES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                    </select>
                    <div style={{ flex: 1 }} />
                    <button onClick={() => window.print()} disabled={loading} style={btn('#2563eb', loading)}>🖨 Print</button>
                </div>

                {err && <div style={styles.error}>{String(err)}</div>}
            </div>

            <div className="hr-print">
                <div className="hr-doc-head" style={{ margin: '18px 0 10px' }}>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>Safety Incentive Program — {active.label}</div>
                    <div style={styles.muted}>
                        {RANGES.find((r) => r.v === monthsBack)?.label}
                        <span className="hr-printed"> · Printed {new Date().toLocaleDateString('en-US')}</span>
                    </div>
                </div>

                {loading ? <div style={styles.placeholder}>Loading…</div>
                 : rows.length === 0 ? <div style={styles.placeholder}>No data for this range yet.</div>
                 : tab === 'points' ? (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Month</th>
                                <th style={styles.th}>Site group</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>Leaders</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>Earned</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>Possible</th>
                                <th style={styles.th}>Achieved</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pointMonths.map((g) => (
                                <React.Fragment key={`${g.year}-${g.month}`}>
                                    {g.sites.map((r, i) => (
                                        <tr key={r.siteGroup}>
                                            <td style={styles.td}>{i === 0 ? monthLabel(g.year, g.month) : ''}</td>
                                            <td style={styles.td}>{r.siteGroup}</td>
                                            <td style={{ ...styles.td, textAlign: 'right' }}>{r.supervisorsEarning}/{r.supervisors}</td>
                                            <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>{r.pointsEarned}</td>
                                            <td style={{ ...styles.td, textAlign: 'right', color: '#6b7280' }}>{r.pointsPossible}</td>
                                            <td style={styles.td}><Bar value={pct(r.pointsEarned, r.pointsPossible)} /></td>
                                        </tr>
                                    ))}
                                    {g.sites.length > 1 && (
                                        <tr style={{ background: '#f9fafb' }}>
                                            <td style={styles.tdTotal}></td>
                                            <td style={styles.tdTotal}>All sites</td>
                                            <td style={{ ...styles.tdTotal, textAlign: 'right' }}>{g.earning}/{g.sups}</td>
                                            <td style={{ ...styles.tdTotal, textAlign: 'right' }}>{g.earned}</td>
                                            <td style={{ ...styles.tdTotal, textAlign: 'right' }}>{g.possible}</td>
                                            <td style={styles.tdTotal}><Bar value={pct(g.earned, g.possible)} /></td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                 ) : tab === 'recordables' ? (
                    <>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Month</th>
                                    {recordGrid.groups.map((g) => <th key={g} style={{ ...styles.th, textAlign: 'center' }}>{g}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {recordGrid.months.map((m) => (
                                    <tr key={`${m.year}-${m.month}`}>
                                        <td style={styles.td}>{monthLabel(m.year, m.month)}</td>
                                        {recordGrid.groups.map((g) => {
                                            const c = m.cells[g];
                                            const txt = !c ? '—' : c.hadRecordable ? 'Recordable' : (c.unanswered ? 'not answered' : 'Clean');
                                            const col = !c ? '#9ca3af' : c.hadRecordable ? '#b91c1c' : (c.unanswered ? '#b45309' : '#15803d');
                                            return <td key={g} style={{ ...styles.td, textAlign: 'center', color: col, fontWeight: 700 }}>{txt}</td>;
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={{ ...styles.muted, marginTop: 8 }}>
                            Totals:{' '}
                            {recordGrid.groups.map((g) => {
                                const n = recordGrid.months.filter((m) => m.cells[g]?.hadRecordable).length;
                                return <span key={g} style={{ marginRight: 14 }}><strong>{g}</strong>: {n} month{n === 1 ? '' : 's'} with a recordable</span>;
                            })}
                        </div>
                    </>
                 ) : tab === 'criteria' ? (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Criterion</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>Passed</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>Of</th>
                                <th style={styles.th}>Pass rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.criterion}>
                                    <td style={styles.td}>{r.criterion}</td>
                                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>{r.passed}</td>
                                    <td style={{ ...styles.td, textAlign: 'right', color: '#6b7280' }}>{r.total}</td>
                                    <td style={styles.td}><Bar value={pct(r.passed, r.total)} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                 ) : (
                    <div>
                        {winnerMonths.map((m) => (
                            <div key={`${m.year}-${m.month}`} style={{ marginBottom: 16 }}>
                                <div style={styles.monthHead}>{monthLabel(m.year, m.month)}</div>
                                {m.sites.map(([site, list]) => (
                                    <div key={site} style={{ marginTop: 6 }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{site || '—'}</div>
                                        <ol style={{ margin: '4px 0 0 20px', fontSize: 13, color: '#111827' }}>
                                            {list.sort((a, b) => a.displayOrder - b.displayOrder).map((w, i) => (
                                                <li key={i}>{w.winnerName}</li>
                                            ))}
                                        </ol>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                 )}
            </div>
        </div>
    );
}

// Simple inline percentage bar (prints fine — no canvas/SVG dependency).
function Bar({ value }) {
    const color = value >= 80 ? '#15803d' : value >= 50 ? '#d97706' : '#dc2626';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: '0 0 110px', height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, value)}%`, height: '100%', background: color }} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color }}>{value}%</span>
        </div>
    );
}

const btn = (bg, disabled) => ({
    background: disabled ? '#cbd5e1' : bg, color: 'white', border: 'none', borderRadius: 6,
    padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
});

const styles = {
    wrap: { padding: 24, maxWidth: 1000 },
    eyebrow: { fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: '#9ca3af', fontWeight: 700 },
    h2: { margin: '2px 0 4px', fontSize: 22, fontWeight: 800, color: '#111827' },
    muted: { color: '#6b7280', fontSize: 13, lineHeight: 1.5, margin: '4px 0' },
    tabBar: { display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb', marginTop: 16 },
    tab: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '8px 14px', fontSize: 14, fontWeight: 600, color: '#6b7280', cursor: 'pointer' },
    tabActive: { color: '#2563eb', borderBottomColor: '#2563eb' },
    controls: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, flexWrap: 'wrap' },
    label: { fontSize: 13, fontWeight: 600, color: '#374151' },
    input: { padding: '6px 10px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, minWidth: 160 },
    error: { marginTop: 12, padding: 10, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13 },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: 6 },
    th: { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#6b7280', borderBottom: '2px solid #e5e7eb', padding: '6px 8px' },
    td: { fontSize: 13, color: '#111827', borderBottom: '1px solid #f3f4f6', padding: '6px 8px' },
    tdTotal: { fontSize: 13, fontWeight: 800, color: '#111827', borderBottom: '1px solid #e5e7eb', padding: '6px 8px' },
    monthHead: { fontSize: 14, fontWeight: 800, color: '#111827', borderBottom: '2px solid #111827', paddingBottom: 3 },
    placeholder: { marginTop: 12, padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13, border: '1px dashed #d1d5db', borderRadius: 8 },
};

const PRINT_CSS = `
.hr-printed { display: none; }
@media print {
    .hr-noprint { display: none !important; }
    .hr-printed { display: inline; }
    .hr-doc-head { border-bottom: 2px solid #111827; padding-bottom: 6px; margin-bottom: 12px; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
    table { width: 100%; border-collapse: collapse; }
    th, td { font-size: 12px; }
}`;

export default HistoricReportsManager;
